if (!customElements.get('newsletter-ajax-form')) {
  class NewsletterAjaxForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.status = this.querySelector('.newsletter-form__status');
      this.submitButton = this.form?.querySelector('[type="submit"]');
      this.responseFrame = this.querySelector('[data-newsletter-response]');

      if (!this.form || !this.status || !this.submitButton || !this.responseFrame) return;

      this.awaitingResponse = false;
      this.handleSubmit = this.handleSubmit.bind(this);
      this.handleResponse = this.handleResponse.bind(this);

      // Observe at window level before Shopify's CAPTCHA listener. Do not cancel
      // the event: Shopify must own the protected customer-form submission.
      window.addEventListener('submit', this.handleSubmit, true);
      this.responseFrame.addEventListener('load', this.handleResponse);
    }

    disconnectedCallback() {
      window.removeEventListener('submit', this.handleSubmit, true);
      this.responseFrame?.removeEventListener('load', this.handleResponse);
      window.clearTimeout(this.responseTimeout);
      window.clearTimeout(this.loadingTimeout);
    }

    handleSubmit(event) {
      if (event.target !== this.form || !this.form.checkValidity()) return;

      this.awaitingResponse = true;
      this.hideStatus();

      // Deferring the disabled state lets the current native submit finish. It
      // also covers the async CAPTCHA hand-off without removing form fields.
      this.loadingTimeout = window.setTimeout(() => {
        if (this.awaitingResponse) this.setLoading(true);
      }, 0);

      window.clearTimeout(this.responseTimeout);
      this.responseTimeout = window.setTimeout(() => {
        if (!this.awaitingResponse) return;

        this.finishSubmission();
        this.showStatus(this.dataset.errorMessage, 'error');
      }, 60000);
    }

    handleResponse() {
      if (!this.awaitingResponse) return;

      try {
        const responseDocument = this.responseFrame.contentDocument;
        const responseUrl = new URL(this.responseFrame.contentWindow.location.href);

        if (responseUrl.href === 'about:blank' || !responseDocument) return;

        const returnedForm = this.form.id
          ? responseDocument.getElementById(this.form.id)
          : null;
        const successMessage = returnedForm?.querySelector('.form-success')
          || responseDocument.querySelector('.newsletter-form--wrapper .form-success');
        const errorMessage = returnedForm?.querySelector('.errors')
          || responseDocument.querySelector('.newsletter-form--wrapper .errors');
        const isChallenge = responseUrl.pathname.startsWith('/challenge')
          || Boolean(responseDocument.querySelector('.shopify-challenge__container, form[action*="/challenge"]'));

        if (isChallenge) {
          throw new Error('Shopify requested an additional CAPTCHA challenge');
        }

        if (errorMessage?.textContent?.trim()) {
          this.finishSubmission();
          this.showStatus(errorMessage.textContent.trim(), 'error');
          return;
        }

        // Shopify returns customer_posted=true for a normal success. Existing
        // subscribers can return the submitted customer form without an
        // explicit success message, so recognize that response separately.
        const returnedEmail = returnedForm
          ?.querySelector('[name="contact[email]"]')
          ?.value
          ?.trim();
        const wasExistingSubscriber = responseUrl.searchParams.get('form_type') === 'customer'
          && Boolean(returnedEmail);
        const wasSuccessful = Boolean(successMessage)
          || responseUrl.searchParams.get('customer_posted') === 'true'
          || wasExistingSubscriber;

        if (wasSuccessful) {
          this.finishSubmission();
          this.showStatus(
            successMessage?.textContent?.trim() || this.dataset.successMessage,
            'success'
          );
          this.form.reset();
          return;
        }

        throw new Error('Newsletter response did not include a success or error state');
      } catch (error) {
        // Shopify can complete its protected customer-form submission on a
        // different shop origin. The browser blocks inspection of that frame,
        // but reaching this load event confirms the native submission finished.
        if (error?.name === 'SecurityError') {
          this.finishSubmission();
          this.showStatus(this.dataset.successMessage, 'success');
          this.form.reset();
          return;
        }

        console.error('Newsletter submission failed:', error);
        this.finishSubmission();
        this.showStatus(this.dataset.errorMessage, 'error');
      }
    }

    finishSubmission() {
      this.awaitingResponse = false;
      window.clearTimeout(this.responseTimeout);
      window.clearTimeout(this.loadingTimeout);
      this.setLoading(false);
    }

    setLoading(isLoading) {
      this.form.toggleAttribute('aria-busy', isLoading);
      this.submitButton.disabled = isLoading;
      this.submitButton.classList.toggle('is-loading', isLoading);
    }

    hideStatus() {
      this.status.hidden = true;
      this.status.textContent = '';
      this.status.classList.remove('newsletter-form__status--success', 'newsletter-form__status--error');
    }

    showStatus(message, type) {
      this.status.textContent = message;
      this.status.classList.add(`newsletter-form__status--${type}`);
      this.status.hidden = false;
    }
  }

  customElements.define('newsletter-ajax-form', NewsletterAjaxForm);
}
